import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy, Square Migration" },
      {
        name: "description",
        content:
          "Privacy Policy for Square Migration. Learn how we collect, use, and protect your information.",
      },
      { property: "og:title", content: "Privacy Policy, Square Migration" },
      {
        property: "og:description",
        content:
          "Privacy Policy for Square Migration. Learn how we collect, use, and protect your information.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-background px-6 py-20 md:py-24">
      <article className="max-w-3xl mx-auto prose prose-invert">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
          Privacy Policy
        </h1>

        <p className="text-muted-foreground">
          Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <section className="mt-8">
          <h2 className="text-xl font-semibold text-foreground">1. Introduction</h2>
          <p className="text-muted-foreground mt-2">
            Square Migration (“we,” “us,” or “our”) respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">2. Information We Collect</h2>
          <p className="text-muted-foreground mt-2">
            We may collect personal information you voluntarily provide, such as your name, email address, phone number, and company details. We also collect non-personal information automatically, including IP addresses, browser type, device information, and usage data through cookies and analytics tools.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">3. How We Use Your Information</h2>
          <ul className="list-disc pl-5 mt-2 text-muted-foreground space-y-1">
            <li>To provide, operate, and maintain our services</li>
            <li>To communicate with you, including responding to inquiries</li>
            <li>To improve our website, products, and user experience</li>
            <li>To comply with legal obligations and protect our rights</li>
          </ul>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">4. Sharing Your Information</h2>
          <p className="text-muted-foreground mt-2">
            We do not sell your personal information. We may share data with trusted service providers who assist in operating our business, such as hosting, analytics, and payment processing, subject to confidentiality obligations.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">5. Cookies and Tracking</h2>
          <p className="text-muted-foreground mt-2">
            We use cookies and similar technologies to enhance your experience and analyze site traffic. You can control cookies through your browser settings.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">6. Data Security</h2>
          <p className="text-muted-foreground mt-2">
            We implement reasonable administrative, technical, and physical safeguards to protect your information. However, no method of transmission over the internet is completely secure.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">7. Your Rights</h2>
          <p className="text-muted-foreground mt-2">
            Depending on your location, you may have rights to access, correct, delete, or restrict the use of your personal information. Contact us to exercise these rights.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">8. Changes to This Policy</h2>
          <p className="text-muted-foreground mt-2">
            We may update this Privacy Policy from time to time. Changes are effective when posted with a revised “Last updated” date.
          </p>
        </section>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-foreground">9. Contact Us</h2>
          <p className="text-muted-foreground mt-2">
            If you have questions about this Privacy Policy, please contact us at{" "}
            <a
              href="mailto:chris@squaremigration.com"
              className="text-primary hover:underline"
            >
              chris@squaremigration.com
            </a>
            .
          </p>
        </section>
      </article>
    </main>
  );
}
